import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HomeIcon, BatchIcon, EditorIcon, GalleryIcon, MoodboardIcon, BrandbookIcon,
  NotificationIcon, HomeDecorIcon, DiamondIcon, AdminIcon, ChevronDownIcon,
  StillsIcon, ShirtIcon, PackageIcon, EyewearIcon, JewelryIcon, VideoIcon, TrendForgeIcon, ApiIcon,
  UsersIcon, SparklesIcon, HelpCircleIcon, UserIcon, WorkflowIcon,
  SunIcon, MoonIcon, StorefrontIcon
} from './icons/Icons';
import { Logo } from './icons/Logo';
import UserProfile from './UserProfile';
import NotificationModal from './shared/NotificationModal';
import { BillingService } from '../services/billingService';
import { useAuthStore } from '../stores/useAuthStore';
import { useAuthModalStore } from '../stores/useAuthModalStore';
import { useNotificationStore } from '../stores/useNotificationStore';
import { useFeedbackModalStore } from '../stores/useFeedbackModalStore';
import { useNotificationChannel } from '../hooks/useNotificationChannel';
import { useModules } from '../hooks/useModules';
import { useTheme } from '../hooks/useTheme';
import { Badge } from './ui';
import { isOrdersStaff } from '../utils/ordersStaff';
import { isSnapfyInternal } from '../utils/snapfyInternal';
import { canUseMarketingSegment, getSegment } from '../utils/segment';
import MarketingSidebar from './marketing-segment/MarketingSidebar';
import { APP_HOME } from '../utils/segment';

interface SidebarProps {
  onOpenAllNotifications: () => void;
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
  onStartTour?: () => void;
}

const NavGroup: React.FC<{ title: string; children: React.ReactNode; id?: string }> = ({ title, children, id }) => (
  // Design system glass: grupo sem caixa, rótulo mono em caixa alta (eyebrow do menu).
  <div id={id} className="mb-3">
    <h3 className="px-2.5 pt-2 pb-1.5 block font-mono text-[10px] font-medium leading-none uppercase tracking-[0.08em] text-snapfy-text-placeholder">{title}</h3>
    <div className="space-y-0.5">
      {children}
    </div>
  </div>
);

const NavSubGroup: React.FC<{
  title: string;
  icon: React.ReactElement;
  children: React.ReactNode;
  isActive?: boolean;
}> = ({ title, icon, children, isActive }) => {
  const [isOpen, setIsOpen] = useState(isActive || false);

  useEffect(() => {
    if (isActive) setIsOpen(true);
  }, [isActive]);

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between h-[34px] px-2.5 rounded-control transition-colors duration-150 w-full text-left group focus-visible:outline-none focus-visible:ring-snapfy-focus ${
          isActive ? 'text-snapfy-text-primary bg-snapfy-control' : 'text-snapfy-text-secondary hover:bg-snapfy-control hover:text-snapfy-text-primary'
        }`}
      >
        <div className="flex items-center">
          {React.cloneElement(icon as React.ReactElement<{ className?: string }>, { className: `w-[18px] h-[18px] transition-colors ${isActive ? 'text-snapfy-text-accent' : 'group-hover:text-snapfy-text-accent'}` })}
          <span className="block ml-3 font-medium text-[13px]">{title}</span>
        </div>
        <ChevronDownIcon className={`w-4 h-4 transition-transform block ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="pl-0 lg:pl-3 space-y-0.5 mt-0.5 lg:border-l lg:border-snapfy-border lg:ml-4">
          {children}
        </div>
      )}
    </div>
  );
};

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  active: boolean;
  requiresAuth?: boolean;
  badge?: string;
  disabled?: boolean;
  id?: string;
  onCloseMobile?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, to, active, requiresAuth = true, badge, disabled, id, onCloseMobile }) => {
  const { isAuthenticated } = useAuthStore();
  const { openModal } = useAuthModalStore();

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (requiresAuth && !isAuthenticated) {
      e.preventDefault();
      openModal({ redirectPath: to });
      return;
    }
    onCloseMobile?.();
  };

  return (
    <Link
      to={disabled ? '#' : to}
      onClick={handleClick}
      id={id}
      // Design system glass: 34px, raio 10, 13px; ativo = degradê suave + contorno interno de vidro.
      className={`flex items-center h-[34px] px-2.5 rounded-control transition-colors duration-150 w-full text-left group relative focus-visible:outline-none focus-visible:ring-snapfy-focus ${
        disabled
          ? 'text-snapfy-text-disabled cursor-not-allowed'
          : active
            ? 'bg-snapfy-gradient-active text-snapfy-text-primary shadow-[inset_0_0_0_1px_var(--snapfy-border-glass)]'
            : 'text-snapfy-text-secondary hover:bg-snapfy-control hover:text-snapfy-text-primary'
      }`}
    >
      {React.cloneElement(icon as React.ReactElement<{ className?: string }>, { className: `w-[18px] h-[18px] transition-colors ${active ? 'text-snapfy-text-accent' : 'group-hover:text-snapfy-text-accent'}` })}
      <span className={`block ml-3 text-[13px] flex-1 ${active ? 'font-semibold' : 'font-medium'}`}>{label}</span>
      {badge && (
        <Badge variant="info">
          {badge}
        </Badge>
      )}
    </Link>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ onOpenAllNotifications, isMobileOpen, onCloseMobile, onStartTour }) => {
  const { t, i18n } = useTranslation('common');
  const { t: tOrders } = useTranslation('orders');

  // Seletor de idioma (PT-BR ↔ EN): troca a língua do i18next e persiste a escolha —
  // o init do i18n lê a mesma chave, então a preferência sobrevive ao reload.
  const currentLang = i18n.language === 'en' ? 'en' : 'pt-BR';
  const toggleLanguage = () => {
    const next = currentLang === 'en' ? 'pt-BR' : 'en';
    i18n.changeLanguage(next);
    try {
      window.localStorage.setItem('snapfy_language', next);
    } catch {
      // Sem localStorage (modo privado restrito): a troca vale só para a sessão.
    }
  };
  const { isLightMode, toggleTheme } = useTheme();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const notificationButtonRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated } = useAuthStore();
  const { openModal } = useAuthModalStore();
  const { unreadCount } = useNotificationStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { isModuleVisible, getModuleStatus, loaded: modulesLoaded } = useModules();

  useNotificationChannel();

  const isVisible = (slug: string): boolean => isModuleVisible(slug);

  const getStatusBadge = (slug: string): string | undefined => {
    const status = getModuleStatus(slug);
    if (status === 'development') return t('sidebar.comingSoon');
    if (status === 'maintenance') return t('moduleStatus.maintenance', 'Em Manutenção');
    return undefined;
  };

  const isDisabledByModule = (slug: string): boolean => {
    const status = getModuleStatus(slug);
    return status === 'development' || status === 'maintenance';
  };

  const handleUpgradeClick = () => {
    if (!isAuthenticated) {
      openModal({ redirectPath: '/profile' });
      return;
    }
    navigate('/profile?tab=purchase');
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setCredits(null);
      return;
    }
    const fetchCredits = async () => {
      try {
        const balance = await BillingService.getBalance();
        setCredits(balance);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      }
    };
    fetchCredits();
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleViewAllNotifications = () => {
    setIsNotificationsOpen(false);
    onOpenAllNotifications();
  };

  const closeMobile = () => onCloseMobile?.();

  const isActive = (path: string) => {
    if (path === APP_HOME && location.pathname === APP_HOME) return true;
    if (path !== APP_HOME && location.pathname.startsWith(path)) return true;
    return false;
  };

  // VTO cost center is a tab inside /admin (?tab=vto-costs), not a standalone route.
  const vtoCostsActive = new URLSearchParams(location.search).get('tab') === 'vto-costs';

  const comingSoon = t('sidebar.comingSoon');

  // Segmento Marketing (/marketing/*): menu próprio da plataforma Marketing. Fica
  // depois de todos os hooks para não mudar a ordem deles entre renders.
  if (getSegment(location.pathname) === 'marketing' && canUseMarketingSegment(user)) {
    return (
      <MarketingSidebar
        isMobileOpen={isMobileOpen}
        onCloseMobile={onCloseMobile}
        credits={credits}
        onUpgrade={handleUpgradeClick}
        isLightMode={isLightMode}
        onToggleTheme={toggleTheme}
      />
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={closeMobile}
        />
      )}
      {/* Design system glass: painel de vidro forte; no desktop flutua a 14px das bordas. */}
      <aside className={`fixed lg:sticky top-0 lg:top-3.5 left-0 lg:ml-3.5 h-screen lg:h-[calc(100vh-28px)] z-50 snapfy-glass-strong rounded-r-glass lg:rounded-glass p-3 md:p-4 flex flex-col items-start w-64 md:w-72 transition-transform duration-300 ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      } lg:w-64`}>
        <div className="mb-4 md:mb-6 lg:mb-10 w-full flex justify-between items-center px-1 md:px-2 flex-shrink-0">
          <Link to={APP_HOME} onClick={closeMobile}>
            <Logo className="h-6 md:h-7 w-auto" />
          </Link>
          <button onClick={closeMobile} className="lg:hidden p-1.5 md:p-2 text-snapfy-text-secondary hover:text-snapfy-text-primary">
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 w-full overflow-y-auto custom-scrollbar pr-1 flex flex-col">
          <nav className="flex flex-col space-y-1.5 md:space-y-2 w-full pb-4 md:pb-6 lg:pb-10">
            <NavItem icon={<HomeIcon />} label={t('sidebar.home')} to={APP_HOME} active={isActive(APP_HOME)} requiresAuth={false} id="tour-home" onCloseMobile={closeMobile} />

            {isOrdersStaff(user) && isVisible('orders') && (
              <NavGroup title={t('sidebar.operationsGroupTitle', 'Operação')}>
                <NavItem icon={<PackageIcon />} label={tOrders('adminKanban')} to="/admin/kanban" active={isActive('/admin/kanban')} onCloseMobile={closeMobile} />
                <NavItem icon={<UsersIcon />} label={tOrders('adminClients')} to="/admin/clients" active={isActive('/admin/clients')} onCloseMobile={closeMobile} />
                <NavItem icon={<PackageIcon />} label={tOrders('ordersAdminNav', 'Pedidos')} to="/orders" active={isActive('/orders')} id="tour-pedidos" onCloseMobile={closeMobile} />
              </NavGroup>
            )}

            {!modulesLoaded ? (
              /* Skeleton while modules load */
              <>
                <div className="bg-snapfy-surface-hover/50 border border-snapfy-border rounded-xl p-2 mb-4 space-y-2">
                  <div className="h-3 w-20 bg-snapfy-surface-hover rounded animate-pulse mx-2 mt-1" />
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5">
                      <div className="w-5 h-5 bg-snapfy-surface-hover rounded animate-pulse" />
                      <div className="h-4 bg-snapfy-surface-hover rounded animate-pulse" style={{ width: `${60 + i * 20}px` }} />
                    </div>
                  ))}
                </div>
                <div className="bg-snapfy-surface-hover/50 border border-snapfy-border rounded-xl p-2 mb-4 space-y-2">
                  <div className="h-3 w-12 bg-snapfy-surface-hover rounded animate-pulse mx-2 mt-1" />
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5">
                      <div className="w-5 h-5 bg-snapfy-surface-hover rounded animate-pulse" />
                      <div className="h-4 bg-snapfy-surface-hover rounded animate-pulse" style={{ width: `${50 + i * 15}px` }} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
            <>
            {!isOrdersStaff(user) && isVisible('orders') && (
              <NavGroup title={tOrders('ordersGroupTitle', 'Pedidos')} id="tour-pedidos">
                <NavItem icon={<PackageIcon />} label={tOrders('myOrders')} to="/orders" active={isActive('/orders')} onCloseMobile={closeMobile} />
              </NavGroup>
            )}

            <NavGroup title={t('sidebar.myArea')} id="tour-minha-area">
              {isVisible('gallery') && <NavItem icon={<GalleryIcon />} label={t('sidebar.gallery')} to="/gallery" active={isActive('/gallery')} badge={getStatusBadge('gallery')} disabled={isDisabledByModule('gallery')} onCloseMobile={closeMobile} />}
              {isVisible('brandbook') && <NavItem icon={<BrandbookIcon />} label={t('sidebar.brandbook')} to="/brandbook" active={isActive('/brandbook')} badge={getStatusBadge('brandbook')} disabled={isDisabledByModule('brandbook')} onCloseMobile={closeMobile} />}
              {isVisible('my_models') && <NavItem icon={<UsersIcon />} label={t('sidebar.myModels')} to="/models" active={isActive('/models') && !location.pathname.startsWith('/models/create')} badge={getStatusBadge('my_models')} disabled={isDisabledByModule('my_models')} onCloseMobile={closeMobile} />}
              {!user?.companyId && user?.role !== 'admin' && (
                <NavItem icon={<AdminIcon />} label={t('sidebar.company')} to="/register-company" active={isActive('/register-company')} onCloseMobile={closeMobile} />
              )}
              {user?.companyId && user?.role !== 'admin' && (
                <NavItem icon={<AdminIcon />} label={t('sidebar.company')} to="/organization" active={isActive('/organization')} onCloseMobile={closeMobile} />
              )}
            </NavGroup>

            {/* Meus Apps — apps proprietários do cliente, entre Minha Área e Apps.
                Fachadas ainda não foi validado com cliente: enquanto isso o grupo
                só aparece para contas @snapfy.ai. Abrir é remover esta condição. */}
            {isSnapfyInternal(user) && (
              <NavGroup title={t('sidebar.myApps', 'Meus Apps')}>
                <NavItem icon={<StorefrontIcon />} label={t('facades:title')} to="/facades" active={isActive('/facades')} onCloseMobile={closeMobile} />
              </NavGroup>
            )}

            <NavGroup title={t('sidebar.apps')} id="tour-apps">
              {(isVisible('moodboard') || isVisible('workflow')) && (
                <NavSubGroup
                  title={t('sidebar.creative')}
                  icon={<MoodboardIcon />}
                  isActive={isActive('/moodboard') || isActive('/workflow')}
                >
                  {isVisible('moodboard') && <NavItem icon={<MoodboardIcon />} label={t('sidebar.canvas')} to="/moodboard" active={isActive('/moodboard')} badge={getStatusBadge('moodboard')} disabled={isDisabledByModule('moodboard')} onCloseMobile={closeMobile} />}
                  {isVisible('workflow') && <NavItem icon={<WorkflowIcon />} label={t('sidebar.workflow')} to="/workflow" active={isActive('/workflow')} badge={getStatusBadge('workflow')} disabled={isDisabledByModule('workflow')} onCloseMobile={closeMobile} />}
                </NavSubGroup>
              )}
              {isVisible('model_creator') && <NavItem icon={<UserIcon />} label={t('sidebar.models')} to="/models/create" active={isActive('/models/create')} badge={getStatusBadge('model_creator')} disabled={isDisabledByModule('model_creator')} onCloseMobile={closeMobile} />}
              {isVisible('lookbook') && <NavItem icon={<BatchIcon />} label={t('sidebar.lookbook')} to="/batch" active={isActive('/batch')} badge={getStatusBadge('lookbook')} disabled={isDisabledByModule('lookbook')} onCloseMobile={closeMobile} />}
              {(isVisible('stills_vestuario') || isVisible('stills_produtos')) && (
                <NavSubGroup
                  title={t('sidebar.stills')}
                  icon={<StillsIcon />}
                  isActive={isActive('/stills')}
                >
                  {isVisible('stills_vestuario') && <NavItem icon={<ShirtIcon />} label={t('sidebar.clothing')} to="/stills/vestuario" active={isActive('/stills/vestuario')} badge={getStatusBadge('stills_vestuario')} disabled={isDisabledByModule('stills_vestuario')} onCloseMobile={closeMobile} />}
                  {isVisible('stills_produtos') && <NavItem icon={<PackageIcon />} label={t('sidebar.products')} to="/stills/produtos" active={isActive('/stills/produtos')} badge={getStatusBadge('stills_produtos')} disabled={isDisabledByModule('stills_produtos')} onCloseMobile={closeMobile} />}
                </NavSubGroup>
              )}
              {(isVisible('eyewear') || isVisible('jewelry')) && (
                <NavSubGroup
                  title={t('sidebar.accessories')}
                  icon={<SparklesIcon />}
                  isActive={isActive('/accessories')}
                >
                  {isVisible('eyewear') && <NavItem icon={<EyewearIcon />} label={t('sidebar.eyewear')} to="/accessories/eyewear" active={isActive('/accessories/eyewear')} badge={getStatusBadge('eyewear')} disabled={isDisabledByModule('eyewear')} onCloseMobile={closeMobile} />}
                  {isVisible('jewelry') && <NavItem icon={<JewelryIcon />} label={t('sidebar.jewelry')} to="/accessories/jewelry" active={isActive('/accessories/jewelry')} badge={getStatusBadge('jewelry')} disabled={isDisabledByModule('jewelry')} onCloseMobile={closeMobile} />}
                </NavSubGroup>
              )}
              {isVisible('homedecor') && <NavItem icon={<HomeDecorIcon />} label={t('sidebar.homeDecor')} to="/homedecor" active={isActive('/homedecor')} badge={getStatusBadge('homedecor')} disabled={isDisabledByModule('homedecor')} onCloseMobile={closeMobile} />}
              {isVisible('editor') && <NavItem icon={<EditorIcon />} label={t('sidebar.editor')} to="/editor" active={isActive('/editor')} badge={getStatusBadge('editor')} disabled={isDisabledByModule('editor')} onCloseMobile={closeMobile} />}
              {isVisible('veo_studio') && <NavItem icon={<VideoIcon />} label={t('sidebar.videos')} to="/video" active={isActive('/video')} badge={getStatusBadge('veo_studio')} disabled={isDisabledByModule('veo_studio')} onCloseMobile={closeMobile} />}
              {isVisible('marketing_studio') && <NavItem icon={<SparklesIcon />} label={t('sidebar.marketingStudio', 'Marketing Studio')} to="/marketing-studio" active={isActive('/marketing-studio')} badge={getStatusBadge('marketing_studio')} disabled={isDisabledByModule('marketing_studio')} onCloseMobile={closeMobile} />}
              {isVisible('trendforge') && <NavItem icon={<TrendForgeIcon />} label={t('sidebar.trendforge')} to="/trendforge" active={false} badge={getStatusBadge('trendforge') || comingSoon} disabled onCloseMobile={closeMobile} />}
              {isVisible('api_access') && <NavItem icon={<ApiIcon />} label={t('sidebar.api')} to="/api" active={false} badge={getStatusBadge('api_access') || comingSoon} disabled onCloseMobile={closeMobile} />}
            </NavGroup>

            {user?.role === 'admin' && (
              <>
                <NavGroup title={t('sidebar.admin')}>
                  <NavItem icon={<AdminIcon />} label={t('sidebar.adminPanel')} to="/admin" active={isActive('/admin') && !location.pathname.startsWith('/admin/companies') && !location.pathname.startsWith('/admin/vto-') && !vtoCostsActive} onCloseMobile={closeMobile} />
                  <NavItem icon={<AdminIcon />} label={t('sidebar.companies')} to="/admin/companies" active={isActive('/admin/companies')} onCloseMobile={closeMobile} />
                  <NavItem icon={<SparklesIcon />} label={t('sidebar.marketingTemplates', 'Marketing Templates')} to="/admin/marketing-templates" active={isActive('/admin/marketing-templates')} onCloseMobile={closeMobile} />
                  <NavSubGroup
                    title={t('sidebar.vto')}
                    icon={<SparklesIcon />}
                    isActive={location.pathname.startsWith('/admin/vto-') || vtoCostsActive}
                  >
                    <NavItem icon={<UsersIcon />} label={t('sidebar.vtoAttribution')} to="/admin/vto-attribution" active={isActive('/admin/vto-attribution')} onCloseMobile={closeMobile} />
                    <NavItem icon={<GalleryIcon />} label={t('sidebar.vtoSessions')} to="/admin/vto-sessions" active={isActive('/admin/vto-sessions')} onCloseMobile={closeMobile} />
                    <NavItem icon={<DiamondIcon />} label={t('sidebar.vtoCosts')} to="/admin?tab=vto-costs" active={vtoCostsActive} onCloseMobile={closeMobile} />
                  </NavSubGroup>
                </NavGroup>
              </>
            )}
            </>
            )}

            {/* Help & Support — inside scrollable nav area */}
            <div className="space-y-1 pt-2">
              <Link
                to="/help"
                onClick={closeMobile}
                className="flex items-center p-2.5 rounded-xl text-snapfy-text-secondary hover:bg-snapfy-surface-hover hover:text-snapfy-text-primary transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="ml-4 font-semibold text-sm">{t('sidebar.help')}</span>
              </Link>
              <button
                onClick={() => { useFeedbackModalStore.getState().openModal(); closeMobile(); }}
                className="w-full flex items-center p-2.5 rounded-xl bg-gradient-to-r from-snapfy-primary/20 to-snapfy-secondary/20 border border-snapfy-primary/30 text-snapfy-text-secondary hover:from-snapfy-primary/30 hover:to-snapfy-secondary/30 hover:text-snapfy-text-primary transition-all group"
              >
                <svg className="w-5 h-5 text-snapfy-accent-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="ml-4 font-semibold text-sm">{t('sidebar.supportFeedback')}</span>
              </button>
            </div>
            {/* Notifications — inside scroll area so sidebar items are not cut off */}
            {isAuthenticated && (
              <div ref={notificationButtonRef} className="relative">
                <button
                  onClick={() => setIsNotificationsOpen(prev => !prev)}
                  className="flex items-center p-2 md:p-2.5 rounded-xl text-snapfy-text-secondary w-full text-left relative hover:bg-snapfy-surface-hover hover:text-snapfy-text-primary transition-all group"
                >
                  <NotificationIcon className="w-4 h-4 md:w-5 md:h-5 group-hover:text-snapfy-primary transition-colors" />
                  <span className="ml-3 md:ml-4 font-semibold text-sm">{t('sidebar.notifications')}</span>
                  {unreadCount > 0 && (
                    <span className="absolute left-5 md:left-6 top-1 md:top-1.5 w-3.5 h-3.5 md:w-4 md:h-4 bg-snapfy-primary text-white text-[9px] md:text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-snapfy-surface">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <NotificationModal
                  isOpen={isNotificationsOpen}
                  onClose={() => setIsNotificationsOpen(false)}
                  anchorRef={notificationButtonRef}
                  onViewAll={handleViewAllNotifications}
                />
              </div>
            )}
          </nav>

          {/* Bottom section — inside scroll, pushed to bottom by mt-auto */}
          <div className="mt-auto flex flex-col items-start w-full space-y-3 md:space-y-4 pt-3 md:pt-4 border-t border-snapfy-border pb-4 md:pb-0 flex-shrink-0">
          {isAuthenticated && (
            <div className="w-full mb-2">
              <div className="bg-gradient-to-br from-snapfy-surface-hover to-snapfy-surface rounded-xl md:rounded-2xl p-3 md:p-4 border border-snapfy-border shadow-lg relative overflow-hidden group">
                {user && !user.emailVerifiedAt ? (
                  <>
                    <p className="font-bold text-xs md:text-sm text-snapfy-warning">{t('sidebar.activateAccount')}</p>
                    <button
                      onClick={() => { navigate('/verify-email'); closeMobile(); }}
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-1.5 md:py-2 px-3 rounded-lg md:rounded-xl mt-1 text-xs"
                    >
                      {t('sidebar.verifyEmail')}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="font-bold text-xs md:text-sm text-snapfy-text-primary">{typeof credits === 'number' ? `${credits.toLocaleString('pt-BR')} ${t('sidebar.credits')}` : t('sidebar.loadingCredits')}</p>
                    <p className="text-[10px] md:text-[11px] text-snapfy-text-secondary mb-2 md:mb-3">{t('sidebar.monthlyQuota')}</p>
                    {!((user as any)?.companyId && (user as any)?.companyRole !== 'admin') && (
                      <button
                        onClick={() => { handleUpgradeClick(); closeMobile(); }}
                        className="w-full snapfy-gradient hover:opacity-90 text-white text-[10px] md:text-xs font-bold py-1.5 md:py-2 rounded-lg md:rounded-xl flex items-center justify-center gap-1.5 md:gap-2 shadow-lg shadow-snapfy-primary/20 transition-all active:scale-95"
                      >
                        <DiamondIcon className="w-3 h-3 md:w-3.5 md:h-3.5" />
                        {t('sidebar.upgrade', 'Upgrade')}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          <UserProfile onOpenUpgradeModal={handleUpgradeClick} />
          <div className="flex w-full gap-1.5 md:gap-2">
            <button
              onClick={() => { onStartTour?.(); closeMobile(); }}
              className="flex items-center p-2 md:p-2.5 rounded-xl text-snapfy-text-secondary flex-1 text-left hover:bg-snapfy-surface-hover hover:text-snapfy-text-primary transition-all group justify-start"
              title={t('sidebar.tour')}
            >
              <HelpCircleIcon className="w-4 h-4 md:w-5 md:h-5 group-hover:text-snapfy-text-primary transition-colors" />
              <span className="inline ml-2 md:ml-4 font-semibold text-xs md:text-sm">{t('sidebar.tour')}</span>
            </button>
            <button
              onClick={() => { toggleTheme(); closeMobile(); }}
              className="flex items-center p-2 md:p-2.5 rounded-xl text-snapfy-text-secondary flex-1 text-left hover:bg-snapfy-surface-hover hover:text-snapfy-text-primary transition-all group justify-start"
              title={isLightMode ? t('sidebar.darkMode', 'Escuro') : t('sidebar.lightMode', 'Claro')}
            >
              {isLightMode ? (
                <MoonIcon className="w-4 h-4 md:w-5 md:h-5 group-hover:text-snapfy-text-primary transition-colors" />
              ) : (
                <SunIcon className="w-4 h-4 md:w-5 md:h-5 group-hover:text-snapfy-text-primary transition-colors" />
              )}
              <span className="inline ml-2 md:ml-4 font-semibold text-xs md:text-sm">{isLightMode ? t('sidebar.darkMode', 'Escuro') : t('sidebar.lightMode', 'Claro')}</span>
            </button>
            {/* Seletor de idioma — mostra a língua atual; o clique alterna PT-BR ↔ EN. */}
            <button
              onClick={toggleLanguage}
              className="flex items-center p-2 md:p-2.5 rounded-xl text-snapfy-text-secondary text-left hover:bg-snapfy-surface-hover hover:text-snapfy-text-primary transition-all group justify-start"
              title={t('sidebar.language', 'Idioma')}
              aria-label={t('sidebar.language', 'Idioma')}
            >
              <svg className="w-4 h-4 md:w-5 md:h-5 group-hover:text-snapfy-text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1018 0 9 9 0 00-18 0zm0 0h18M12 3a15.3 15.3 0 014 9 15.3 15.3 0 01-4 9 15.3 15.3 0 01-4-9 15.3 15.3 0 014-9z" />
              </svg>
              <span className="inline ml-2 font-semibold text-xs md:text-sm uppercase">{currentLang === 'en' ? 'EN' : 'PT'}</span>
            </button>
          </div>
        </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
